import React, { useEffect, useMemo, useRef, useState } from "react";
import { Input, Segmented } from "antd";
import ReactMarkdown from "react-markdown";
import { LuArrowUp, LuArrowRight } from "react-icons/lu";
import SandboxIcon from "./SandboxIcon";
import { useDashboard } from "../Contexts/dashboardHooks";
import { useExperimentalFlag } from "../Components/experimentalFlags";
import { useSandboxAI } from "./SandboxAIContext";
import MutationChip from "./MutationChip";
import ReviewBar from "./ReviewBar";
import CompareDiff from "./CompareDiff";
import type { SandboxAIMessage } from "./types";
import {
  BUILD_INTENT_RE,
  BUILDING_MESSAGE,
  EXAMPLE_PROMPTS_DEFAULT,
  EXAMPLE_PROMPTS_DEVELOPMENT,
  EXAMPLE_PROMPTS_EMPTY,
  EXAMPLE_PROMPTS_WIDGET_TESTS,
  WORKING_COPY_ROTATE_MS,
  WORKING_MESSAGES,
} from "./config";
import type { WidgetTestPrompt } from "./config";

const { TextArea } = Input;

// Elapsed-seconds + rotating neutral copy while the POST is in flight. For long
// build-from-scratch turns it swaps to a "Building your dashboard…" line.
const WorkingIndicator: React.FC<{ input: string }> = ({ input }) => {
  const [elapsed, setElapsed] = useState(0);
  const [copyIdx, setCopyIdx] = useState(0);
  const isBuild = BUILD_INTENT_RE.test(input);

  useEffect(() => {
    const start = Date.now();
    const tick = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    const rotate = setInterval(() => setCopyIdx((i) => i + 1), WORKING_COPY_ROTATE_MS);
    return () => {
      clearInterval(tick);
      clearInterval(rotate);
    };
  }, []);

  const label = isBuild ? BUILDING_MESSAGE : WORKING_MESSAGES[copyIdx % WORKING_MESSAGES.length];
  return (
    <div className="sandbox-ai-msg sandbox-ai-msg-assistant">
      <div className="sandbox-ai-bubble sandbox-ai-bubble-ai sandbox-ai-working">
        <span className="sandbox-ai-working-dots">●●●</span>
        <span className="sandbox-ai-working-label">{label}</span>
        <span className="sandbox-ai-working-elapsed">{elapsed}s</span>
      </div>
    </div>
  );
};

// Plain strings render (and send) as-is; widget-test entries render their short
// label but send the long test prompt.
const PromptList: React.FC<{
  prompts: ReadonlyArray<string | WidgetTestPrompt>;
  onPick: (p: string) => void;
  scrollable?: boolean;
}> = ({ prompts, onPick, scrollable }) => (
  <div className={`sandbox-ai-empty-prompts${scrollable ? " sandbox-ai-empty-prompts-scroll" : ""}`}>
    {prompts.map((p) => {
      const label = typeof p === "string" ? p : p.label;
      const prompt = typeof p === "string" ? p : p.prompt;
      return (
        <button
          key={label}
          type="button"
          className="sandbox-ai-empty-prompt-btn"
          onClick={() => onPick(prompt)}
        >
          <span className="sandbox-ai-empty-prompt-text">{label}</span>
          <LuArrowRight className="sandbox-ai-empty-prompt-arrow" />
        </button>
      );
    })}
  </div>
);

type EmptyStateTab = "Standard" | "Development" | "Widget Tests";

const EmptyState: React.FC<{ prompts: string[]; onPick: (p: string) => void }> = ({
  prompts,
  onPick,
}) => {
  const devEnabled = useExperimentalFlag("advancedSandboxAiDefaults");
  const [tab, setTab] = useState<EmptyStateTab>("Standard");
  const tabPrompts: ReadonlyArray<string | WidgetTestPrompt> =
    tab === "Development"
      ? EXAMPLE_PROMPTS_DEVELOPMENT
      : tab === "Widget Tests"
        ? EXAMPLE_PROMPTS_WIDGET_TESTS
        : prompts;
  return (
    <div className="sandbox-ai-empty">
      <div className="sandbox-ai-empty-hero">
        <div className="sandbox-ai-empty-badge">
          <SandboxIcon />
        </div>
        <div className="sandbox-ai-empty-headline">What should we build?</div>
        <div className="sandbox-ai-empty-sub">
          Describe a change in plain language and I&apos;ll build, edit, or rearrange your
          dashboard.
        </div>
      </div>
      {devEnabled ? (
        <>
          <Segmented
            block
            size="small"
            className="sandbox-ai-empty-tabs"
            value={tab}
            onChange={(v) => setTab(v as EmptyStateTab)}
            options={["Standard", "Development", "Widget Tests"]}
          />
          <PromptList prompts={tabPrompts} onPick={onPick} scrollable={tab !== "Standard"} />
        </>
      ) : (
        <>
          <div className="sandbox-ai-empty-label">Try one of these</div>
          <PromptList prompts={prompts} onPick={onPick} />
        </>
      )}
    </div>
  );
};

const SandboxAIPanel: React.FC = () => {
  const {
    open, status, messages, turnSnapshot,
    closePanel, sendMessage, cancelTurn, keepChanges, undoChanges, retryLast,
  } = useSandboxAI();
  const { state } = useDashboard();

  const devEnabled = useExperimentalFlag("advancedSandboxAiDefaults");
  const [input, setInput] = useState("");
  // TEMP(debug): tracks which message's raw response was just copied.
  const [copiedId, setCopiedId] = useState<string | null>(null);
  // TEMP(debug): "Copied ✓" feedback for the whole-conversation copy button.
  const [copiedConvo, setCopiedConvo] = useState(false);

  // TEMP(debug): copy a turn's raw backend JSON to the clipboard.
  const copyRaw = (m: SandboxAIMessage) => {
    if (!m.raw) return;
    const done = () => {
      setCopiedId(m.id);
      window.setTimeout(() => setCopiedId((cur) => (cur === m.id ? null : cur)), 1500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(m.raw).then(done).catch(done);
    } else {
      done();
    }
  };

  // TEMP(debug): copy the entire conversation as one JSON object. The stringified
  // debug payloads (raw backend response, before/after specs) are parsed back into
  // objects so the export is a single well-formed JSON document.
  const copyConversation = () => {
    const parseMaybe = (s?: string) => {
      if (!s) return undefined;
      try {
        return JSON.parse(s);
      } catch {
        return s;
      }
    };
    const payload = {
      exported_at: new Date().toISOString(),
      dashboard_id: state.dashboardId,
      dashboard_name: state.name,
      message_count: messages.length,
      messages: messages.map((m) => ({
        ...m,
        raw: parseMaybe(m.raw),
        diffBefore: parseMaybe(m.diffBefore),
        diffAfter: parseMaybe(m.diffAfter),
      })),
    };
    const done = () => {
      setCopiedConvo(true);
      window.setTimeout(() => setCopiedConvo(false), 1500);
    };
    const text = JSON.stringify(payload, null, 2);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      done();
    }
  };
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, status]);

  const dashboardIsEmpty = Object.keys(state.widgets).length === 0;
  const prompts = dashboardIsEmpty ? EXAMPLE_PROMPTS_EMPTY : EXAMPLE_PROMPTS_DEFAULT;

  const lastUserInput = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i].role === "user") return messages[i].content;
    }
    return "";
  }, [messages]);

  const changeCount = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role === "assistant" && m.ops) return m.ops.filter((o) => !o.error).length;
    }
    return 0;
  }, [messages]);

  if (!open) return null;

  const working = status === "working";

  const handleSend = () => {
    const text = input.trim();
    if (!text || working) return;
    setInput("");
    sendMessage(text);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderMessage = (m: SandboxAIMessage) => {
    if (m.role === "user") {
      return (
        <div key={m.id} className="sandbox-ai-msg sandbox-ai-msg-user">
          <div className="sandbox-ai-bubble sandbox-ai-bubble-user">{m.content}</div>
        </div>
      );
    }
    return (
      <div key={m.id} className="sandbox-ai-msg sandbox-ai-msg-assistant">
        {m.content ? (
          <div
            className={[
              "sandbox-ai-bubble",
              "sandbox-ai-bubble-ai",
              m.error ? "sandbox-ai-bubble-error" : "",
              m.muted ? "sandbox-ai-bubble-muted" : "",
            ].filter(Boolean).join(" ")}
          >
            <ReactMarkdown>{m.content}</ReactMarkdown>
          </div>
        ) : null}
        {m.ops && m.ops.length > 0 ? (
          <div className="sandbox-ai-chips">
            {m.ops.map((op) => (
              <MutationChip key={op.call_id} op={op} />
            ))}
          </div>
        ) : null}
        {m.error ? (
          <button type="button" className="sandbox-ai-retry" onClick={retryLast}>
            Retry
          </button>
        ) : null}
        {/* TEMP(debug): copy the raw backend response / compare specs for this turn. */}
        {devEnabled && (m.raw || (m.diffBefore && m.diffAfter)) ? (
          <div className="sandbox-ai-debug-row">
            {m.raw ? (
              <button
                type="button"
                className="sandbox-ai-debug-copy"
                onClick={() => copyRaw(m)}
                title="Copy the raw backend response JSON (debug)"
              >
                {copiedId === m.id ? "Copied ✓" : "Copy response"}
              </button>
            ) : null}
            {m.diffBefore && m.diffAfter ? (
              <CompareDiff before={m.diffBefore} after={m.diffAfter} />
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className="sandbox-ai-panel" role="dialog" aria-label="Sandbox AI">
      <div className="sandbox-ai-header">
        <span className="sandbox-ai-header-title">
          <SandboxIcon /> Sandbox AI
        </span>
        <div className="sandbox-ai-header-actions">
          {/* TEMP(debug): export the whole conversation as one JSON object. */}
          {devEnabled && messages.length > 0 ? (
            <button
              type="button"
              className="sandbox-ai-header-copy"
              onClick={copyConversation}
              title="Copy the entire conversation as JSON (debug)"
            >
              {copiedConvo ? "Copied ✓" : "Copy conversation"}
            </button>
          ) : null}
          <button
            type="button"
            className="sandbox-ai-close"
            onClick={closePanel}
            aria-label="Close Sandbox AI"
          >
            ×
          </button>
        </div>
      </div>

      <div className="sandbox-ai-messages" ref={listRef}>
        {messages.length === 0 ? (
          <EmptyState prompts={prompts} onPick={sendMessage} />
        ) : (
          messages.map(renderMessage)
        )}
        {working ? <WorkingIndicator input={lastUserInput} /> : null}
      </div>

      {turnSnapshot ? (
        <ReviewBar count={changeCount} onKeep={keepChanges} onUndo={undoChanges} />
      ) : null}

      <div className="sandbox-ai-input-area">
        <TextArea
          className="sandbox-ai-textarea"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask Sandbox AI to edit your dashboard…"
          autoSize={{ minRows: 1, maxRows: 4 }}
        />
        {working ? (
          <button type="button" className="sandbox-ai-cancel-btn" onClick={cancelTurn}>
            Cancel
          </button>
        ) : (
          <button
            type="button"
            className="sandbox-ai-send-btn"
            onClick={handleSend}
            disabled={!input.trim()}
            aria-label="Send"
          >
            <LuArrowUp />
          </button>
        )}
      </div>
    </div>
  );
};

export default SandboxAIPanel;
